import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPinIcon, TicketIcon, TagIcon } from "./icons.jsx";
import { ShareIcon, XMarkIcon } from "@heroicons/react/24/solid";

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  );
}

export default function FeaturedBig({ item }) {
  const [heroImgErr, setHeroImgErr] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const shareRef = useRef(null);

  const eventId = item?.id || item?._id || "1";
  const shareUrl = `${window.location.origin}/events/${eventId}`;
  const shareText = `🎟️ Check out this event: ${item?.title || "Event"}\n${shareUrl}`;

  const copyLink = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert("✅ Link copied to clipboard!");
    } catch {
      alert("Copy failed. Please copy manually:\n" + shareUrl);
    }
  };

  useEffect(() => {
    setHeroImgErr(false);
  }, [item?.image, item?.id]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (showShare && shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShare(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [showShare]);

  const schedule = item?.schedule;
  const scheduleDates =
    schedule?.dates?.length > 0
      ? schedule.dates
      : item?.dateBlock?.date
      ? [item.dateBlock.date]
      : [];
  const scheduleTime =
    schedule?.time ||
    item?.dateBlock?.time ||
    (item?.date?.includes("·") ? item.date.split("·")[1]?.trim() : item?.time || "");
  const hasSchedule = scheduleDates.length > 0 || scheduleTime || item?.dateFull;

  const handleShareNative = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareData = { title: item?.title || "Event", text: `Check out this event: ${item?.title}`, url: shareUrl };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error("Share cancelled/failed", err);
      }
    } else {
      setShowShare((v) => !v);
    }
  };

  return (
    <div
      className="rounded-2xl border border-slate-200/90 bg-white p-3 shadow-[0_8px_30px_rgba(0,0,0,0.06)] sm:p-4 md:p-5"
      onClickCapture={() => {}}
    >
      <div className="grid items-start gap-4 md:grid-cols-[minmax(220px,340px)_1fr] md:gap-5 lg:grid-cols-[minmax(240px,360px)_1fr]">
        <div className="w-full md:max-w-[360px]">
          <div className="overflow-hidden rounded-xl bg-slate-950/95 shadow-md ring-1 ring-slate-200/80 md:rounded-2xl">
            {item.image && !heroImgErr ? (
              <img
                src={item.image}
                alt={item.title ? `${item.title} poster` : "Event poster"}
                className="block w-full max-h-[min(72vh,520px)] object-contain object-center"
                onClick={(e) => e.stopPropagation()}
                onError={() => setHeroImgErr(true)}
              />
            ) : (
              <div className="flex aspect-[3/4] w-full flex-col items-center justify-center bg-gradient-to-br from-amber-100 via-orange-50 to-slate-100 text-sm font-medium text-slate-600">
                Poster unavailable
              </div>
            )}
          </div>
        </div>

        <div className="relative rounded-xl border border-amber-200/60 bg-gradient-to-br from-white via-amber-50/35 to-orange-50/25 p-3 shadow-inner sm:p-4 md:rounded-2xl md:p-5">
          {hasSchedule && (
            <div className="absolute right-3 top-3 z-10 max-w-[200px] rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-center shadow-md md:right-4 md:top-4 md:px-4 md:py-3">
              <div className="font-sans text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Date &amp; time
              </div>
              {schedule?.multiDay && scheduleDates.length > 1 ? (
                <div className="mt-1 space-y-1">
                  {schedule?.slots?.length
                    ? schedule.slots.map((slot, i) => (
                        <div key={`${slot.dayLabel}-${i}`} className="leading-tight">
                          <div className="font-sans text-[13px] font-extrabold text-slate-900 md:text-sm">
                            {slot.dayLabel}
                          </div>
                          <div className="font-sans text-[11px] font-semibold text-slate-600 md:text-xs">
                            {slot.timeLabel}
                          </div>
                        </div>
                      ))
                    : scheduleDates.map((d, i) => (
                        <div
                          key={`${d}-${i}`}
                          className="font-sans text-[13px] font-extrabold leading-tight text-slate-900 md:text-sm"
                        >
                          {d}
                        </div>
                      ))}
                  {!schedule?.slots?.length && scheduleTime && (
                    <div className="pt-0.5 font-sans text-xs font-semibold text-slate-700 md:text-sm">
                      {scheduleTime}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="font-sans text-base font-extrabold text-slate-900 md:text-lg">
                    {scheduleDates[0] || item?.dateFull?.split("•")[0]?.trim()}
                  </div>
                  {scheduleTime && (
                    <div className="font-sans text-xs font-semibold text-slate-700 md:text-sm">
                      {scheduleTime}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <span className="inline-block rounded-full bg-amber-200/80 px-2.5 py-0.5 font-sans text-[11px] font-bold uppercase tracking-wide text-amber-900">
            {item.category || "Music"}
          </span>

          <h3 className="mt-14 font-sans text-lg font-extrabold leading-tight text-slate-900 sm:mt-12 md:mt-3 md:pr-44 md:text-xl">
            {item.title}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-3 font-sans text-sm font-medium text-slate-700">
            <span className="inline-flex items-center gap-2">
              <MapPinIcon /> {item.subtitle || item.location}
            </span>
          </div>

          <h4 className="mt-4 font-sans text-base font-bold text-slate-900 md:text-lg">About the event</h4>
          {item.about && (
            <div
              className="mt-2 overflow-hidden text-ellipsis font-sans text-sm leading-relaxed text-slate-700 md:text-base"
              style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}
              dangerouslySetInnerHTML={{ __html: item.about }}
            />
          )}

          <div className="mt-3 flex flex-wrap items-center gap-4 font-sans text-sm text-slate-700">
            {item.duration && (
              <span className="inline-flex items-center gap-2">
                <ClockIcon /> {item.duration}
              </span>
            )}
            {item.languages && (
              <span className="inline-flex items-center gap-2">
                <TagIcon /> {item.languages}
              </span>
            )}
            {item.age && (
              <span className="inline-flex items-center gap-2">
                <UsersIcon />
                <span className="rounded-md bg-white/80 px-2 py-0.5 text-xs font-semibold text-slate-800">
                  {item.age}
                </span>
              </span>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2 sm:gap-3">
            <Link
              to={`/events/${eventId}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex min-w-[140px] flex-1 items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2.5 font-sans text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-amber-300"
            >
              <TicketIcon /> Book Donor Passes
            </Link>

            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowShare((v) => !v);
                }}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 font-sans text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
              >
                <ShareIcon className="h-4 w-4" />
                Share
              </button>

              {showShare && (
                <div
                  ref={shareRef}
                  className="absolute bottom-full right-0 z-30 mb-2 w-56 rounded-xl border bg-white text-sm shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute -bottom-2 right-6 h-0 w-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white drop-shadow" />

                  <div className="flex items-center justify-between px-3 pb-2 pt-3">
                    <span className="font-semibold text-slate-800">Share Event</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowShare(false);
                      }}
                      aria-label="Close share menu"
                    >
                      <XMarkIcon className="h-4 w-4 text-slate-600" />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      copyLink();
                    }}
                    className="block w-full px-3 py-2 text-left hover:bg-slate-100"
                  >
                    📋 Copy Link
                  </button>

                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 hover:bg-slate-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    💬 Share via WhatsApp
                  </a>

                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 hover:bg-slate-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    📘 Share on Facebook
                  </a>

                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 hover:bg-slate-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    🐦 Share on X (Twitter)
                  </a>
                </div>
              )}
            </div>

            <button type="button" className="hidden" onClick={handleShareNative} />
          </div>
        </div>
      </div>
    </div>
  );
}
